import { defineStore } from 'pinia'

import EventBus, { GlobalEvents } from '@shared/event-bus'
import { StoreState } from '@shared/models/interfaces/store'
import { Attachment } from '@shared/models/proto/base/attachment'
import { IFileUpload } from '@shared/models/proto/base/file-upload'
import { IMessage } from '@shared/models/proto/base/messages'
import { IQuickSignDocument } from '@shared/models/proto/base/messages'
import type { Nullable } from '@shared/models/types/primary'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { INITIAL_PER_PAGE_VARIANTS } from '@shared/utils/constants/pagination'
import { getDataToSet } from '@shared/utils/store/data-to-set'
import {
  getInitialState,
  updateState,
} from '@shared/utils/store/http-request-state'

import { QuickSignDocument_Status } from '@protos/quick_sign_document_pb'

import {
  IQuickSignTemplate,
  IQuickSignTemplateListItem,
} from '@vc/models/proto/base/message'
import {
  CreateQuickSignDTO,
  CreateQuickSignTemplateDTO,
  GetFlattenedDocumentDTO,
  IndexQuickSignTemplateDTO,
  ShowQuickSignDTO,
  ShowQuickSignTemplateDTO,
  UpdateQuickSignDTO,
  UpdateQuickSignTemplateDTO,
} from '@vc/models/proto/dto/quick-sign'
import {
  FlattenedDocumentRO,
  IndexQuickSignTemplateRO,
  ItemQuickSignTemplateRO,
  QuickSignItemRO,
} from '@vc/models/proto/ro/quick-sign'
import { useMessageAttachmentsStore } from '@vc/store//message-attachments'
import { useMessagesStore } from '@vc/store/messages'

export const apiTransport = new ApiTransport(resourcePath, 'vc')

interface State {
  quickSignTemplate: Nullable<IQuickSignTemplate>
  quickSignTemplates: StoreState<IndexQuickSignTemplateRO>
  quickSigns: Record<IMessage['id'], StoreState<IQuickSignDocument>>
}

const state = (): State => ({
  quickSigns: {},
  quickSignTemplates: getInitialState(),
  quickSignTemplate: null,
})

export const useQuickSignStore = defineStore('quick-sign', {
  state,

  actions: {
    async createQuickSignDocument(dto: {
      messageId: number
      file: IFileUpload
      annotation: string
      quickSignTemplateId?: number
    }) {
      const { messageId } = dto
      try {
        this.quickSigns[messageId] = updateState(this.quickSigns[messageId])

        const { data: response } = (await apiTransport.post<
          CreateQuickSignDTO,
          QuickSignItemRO
        >(
          '/attachments/create_quick_sign_document',
          new CreateQuickSignDTO(dto),
          CreateQuickSignDTO,
          QuickSignItemRO
        )) ?? { data: {} }

        if (response) {
          this.quickSigns[messageId] = updateState(
            this.quickSigns[messageId],
            response
          )
        } else {
          delete this.quickSigns[messageId]
        }
      } catch (e) {
        this.quickSigns[messageId] = updateState(
          this.quickSigns[messageId],
          new Error(`${e}`)
        )
      }
    },

    async fetchQuickSign(
      quickSignDocumentId: number,
      messageId: IMessage['id']
    ) {
      const messagesStore = useMessagesStore()
      try {
        this.quickSigns[messageId] = updateState(this.quickSigns[messageId])

        const { data: response } = await apiTransport.get<
          ShowQuickSignDTO,
          QuickSignItemRO
        >(
          `/quick_sign_documents`,
          new ShowQuickSignDTO({ quickSignDocumentId }),
          QuickSignItemRO
        )

        const attachments = response
        if (attachments) {
          this.quickSigns[messageId] = updateState(
            this.quickSigns[messageId],
            attachments
          )
          messagesStore.updateQuickSignDocumentInMessage(attachments)
        } else {
          delete this.quickSigns[messageId]
        }
      } catch (e) {
        this.quickSigns[messageId] = updateState(
          this.quickSigns[messageId],
          new Error(`${e}`)
        )
      }
    },

    async updateQuickSignDocument(
      dto: {
        quickSignDocumentId: number
        status: QuickSignDocument_Status
        annotation: string
      },
      messageId: IMessage['id']
    ) {
      const messagesStore = useMessagesStore()
      try {
        this.quickSigns[messageId] = updateState(this.quickSigns[messageId])
        const params = {
          quickSignDocumentId: dto.quickSignDocumentId,
          data: {
            status: dto.status,
            annotation: dto.annotation,
          },
        }
        const { data } = await apiTransport.put<
          UpdateQuickSignDTO,
          QuickSignItemRO
        >(
          `/quick_sign_documents/${dto.quickSignDocumentId}`,
          new UpdateQuickSignDTO(params),
          UpdateQuickSignDTO,
          QuickSignItemRO,
          {
            onUploadProgress({ total, loaded }) {
              EventBus.emit(GlobalEvents.AsyncRequestProgress, {
                total,
                loaded,
              })
            },
          }
        )
        if (data) {
          this.quickSigns[messageId] = updateState(
            this.quickSigns[messageId],
            data
          )
          messagesStore.updateQuickSignDocumentInMessage(data)
        } else {
          delete this.quickSigns[messageId]
        }
        return data
      } catch (e) {
        this.quickSigns[messageId] = updateState(
          this.quickSigns[messageId],
          new Error(`${e}`)
        )
        throw new Error(`${e}`)
      }
    },

    async fetchFlattenDocument(
      quickSignDocumentId: number,
      messageId?: IMessage['id']
    ) {
      try {
        const response = await apiTransport.get<
          GetFlattenedDocumentDTO,
          FlattenedDocumentRO
        >(
          `/quick_sign_documents/${quickSignDocumentId}/get_flattened_document`,
          new GetFlattenedDocumentDTO({ quickSignDocumentId }),
          FlattenedDocumentRO
        )

        const data = response?.data

        if (messageId && data?.url) {
          this.setFlattenUrlToAttachment(messageId, data.url)
        }

        return data?.url || ''
      } catch (e) {
        throw new Error(`${e}`)
      }
    },

    setFlattenUrlToAttachment(messageId: IMessage['id'], url: string) {
      const messageAttachmentsStore = useMessageAttachmentsStore()
      const attachment = messageAttachmentsStore.getAttachment(messageId)

      if (attachment) {
        if (attachment?.quickSignDocument) {
          messageAttachmentsStore.records[messageId] = updateState(
            messageAttachmentsStore.records[messageId],
            new Attachment({
              ...attachment,
              quickSignDocument: {
                ...(attachment?.quickSignDocument || {}),
                flattenedDocumentUrl: url,
              },
            })
          )
        }
      }
    },

    async saveAsTemplate(payload: {
      name: string
      annotation: string
      chatWidgetId: number
      file: IFileUpload
      isPrivate: boolean
    }) {
      await apiTransport.post<
        CreateQuickSignTemplateDTO,
        ItemQuickSignTemplateRO
      >(
        `quick_sign_templates`,
        new CreateQuickSignTemplateDTO({ data: { ...payload } }),
        CreateQuickSignTemplateDTO,
        ItemQuickSignTemplateRO
      )
    },

    async fetchQuickSignTemplates(payload: {
      page?: number
      chatWidgetId: number
      search: string
      onlyPrivate: boolean
    }) {
      try {
        this.quickSignTemplates = updateState(this.quickSignTemplates)

        const response = await apiTransport.get<
          IndexQuickSignTemplateDTO,
          IndexQuickSignTemplateRO
        >(
          `quick_sign_templates`,
          new IndexQuickSignTemplateDTO({
            ...payload,
            perPage: INITIAL_PER_PAGE_VARIANTS[3],
          }),
          IndexQuickSignTemplateRO
        )

        const { data: existingList } = this.quickSignTemplates.data ?? {}

        const updatedData = getDataToSet<IQuickSignTemplateListItem>(
          payload?.page ?? 1,
          response?.data ?? [],
          (existingList ?? []) as IQuickSignTemplateListItem[]
        )

        this.quickSignTemplates = updateState(
          this.quickSignTemplates,
          new IndexQuickSignTemplateRO({
            data: updatedData,
            meta: response?.meta,
          })
        )
      } catch (e) {
        this.quickSignTemplates = updateState(
          this.quickSignTemplates,
          new Error(`${e}`)
        )
        throw e
      }
    },

    async fetchTemplateById(quickSignTemplateId: number, chatWidgetId: number) {
      const dto = new ShowQuickSignTemplateDTO({
        quickSignTemplateId,
        chatWidgetId,
      })
      // eslint-disable-next-line no-useless-catch
      try {
        this.quickSignTemplate = null
        const response = await apiTransport.get<
          ShowQuickSignTemplateDTO,
          ItemQuickSignTemplateRO
        >(
          `quick_sign_templates/${quickSignTemplateId}`,
          dto,
          ItemQuickSignTemplateRO
        )
        if (response?.data) {
          this.quickSignTemplate = response.data
          return response.data
        }
        return null
      } catch (e) {
        throw e
      }
    },

    async updateTemplate(data: {
      quickSignTemplateId: number
      chatWidgetId: number
      annotation: string
      isPrivate: boolean
    }) {
      const dto = new UpdateQuickSignTemplateDTO({
        quickSignTemplateId: data.quickSignTemplateId,
        chatWidgetId: data.chatWidgetId,
        data: { annotation: data.annotation, isPrivate: data.isPrivate },
      })

      this.quickSignTemplate = null
      const response = await apiTransport.put<
        UpdateQuickSignTemplateDTO,
        ItemQuickSignTemplateRO
      >(
        `quick_sign_templates/${data.quickSignTemplateId}`,
        dto,
        UpdateQuickSignTemplateDTO,
        ItemQuickSignTemplateRO
      )
      if (response?.data) {
        this.quickSignTemplate = response.data
        return response.data
      }
    },
  },

  getters: {
    getQuickSign: (state) => (messageId: IMessage['id']) =>
      state.quickSigns[messageId]?.data || null,
  },
})
